import type { Column } from "@replo/design-system/components/table/Table";
import type { ConstructedAnalyticsLink } from "schemas/generated/analyticsLink";
import type { Experiment } from "schemas/generated/experiment";

import * as React from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { routes } from "@editor/utils/router";

import { NO_LINK_TEXT } from "@/features/experiments/utils";
import { successToast } from "@replo/design-system/components/alert/Toast";
import IconButton from "@replo/design-system/components/button/IconButton";
import Table from "@replo/design-system/components/table/Table";
import { Tag } from "@replo/design-system/components/tag/Tag";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import { experimentStatusMapping } from "@replo/design-system/utils/experimentStatus";
import twMerge from "@replo/design-system/utils/twMerge";
import copy from "copy-to-clipboard";
import {
  format,
  formatDistanceStrict,
  formatDistanceToNowStrict,
} from "date-fns";
import { Copy } from "lucide-react";
import { generatePath, Link } from "react-router-dom";
import { getExperimentStatus } from "schemas/experiment";

type ExperimentTableRow = {
  key: string;
  experiment: Experiment;
  link: ConstructedAnalyticsLink | undefined;
};

type AllExperimentsTableProps = {
  experiments: Experiment[];
  links: ConstructedAnalyticsLink[];
};

export const AllExperimentsTable: React.FC<AllExperimentsTableProps> = ({
  experiments,
  links,
}) => {
  const projectId = useCurrentProjectId();

  const tableData: ExperimentTableRow[] = React.useMemo(() => {
    return experiments.map((experiment) => ({
      key: experiment.id,
      experiment,
      link: links.find(
        (link) =>
          link.id ===
          (experiment.completedAnalyticsLinkId ?? experiment.analyticsLinkId),
      ),
    }));
  }, [experiments, links]);

  const columns: Column<ExperimentTableRow>[] = React.useMemo(() => {
    return [
      {
        key: "name",
        header: "Name",
        renderCell: (row) => {
          const { experiment } = row;
          const status = getExperimentStatus({
            createdAt: experiment.createdAt,
            activatedAt: experiment.activatedAt,
            completedAt: experiment.completedAt,
            archivedAt: experiment.archivedAt,
          });

          const path = Boolean(projectId)
            ? generatePath(
                status === "draft"
                  ? routes.shops.experiments.details
                  : routes.shops.experiments.results,
                {
                  projectId,
                  experimentId: experiment.id,
                },
              )
            : generatePath(
                status === "draft"
                  ? routes.workspace.experiments.details
                  : routes.workspace.experiments.results,
                {
                  workspaceId: experiment.workspaceId,
                  experimentId: experiment.id,
                },
              );

          return (
            <Link
              className="text-primary block truncate hover:underline"
              to={path}
            >
              {experiment.name}
            </Link>
          );
        },
      },
      {
        key: "status",
        header: "Status",
        renderCell: (row) => {
          const status = getExperimentStatus({
            createdAt: row.experiment.createdAt,
            activatedAt: row.experiment.activatedAt,
            completedAt: row.experiment.completedAt,
            archivedAt: row.experiment.archivedAt,
          });

          return (
            <Tag variant={experimentStatusMapping[status].variant} size="base">
              {experimentStatusMapping[status].label}
            </Tag>
          );
        },
      },
      {
        key: "path",
        header: "Path",
        width: "w-[400px]",
        renderCell: (row) => {
          const linkUrl = row.link?.url;
          const hasLinkUrl = Boolean(linkUrl);

          return (
            <div className="flex flex-row items-center gap-x-2">
              <span
                className={twMerge(
                  "truncate",
                  hasLinkUrl ? "text-default" : "text-muted",
                )}
              >
                {linkUrl ?? NO_LINK_TEXT}
              </span>
              {hasLinkUrl && (
                <IconButton
                  tooltipText="Copy experiment URL"
                  variant="tertiary"
                  layoutClassName="h-7 w-7"
                  icon={<Copy className="shrink-0" size={16} />}
                  onClick={() => {
                    copy(linkUrl!);
                    successToast("Experiment URL Copied", "");
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        key: "created",
        header: "Created",
        renderCell: (row) => {
          const createDateRelative = formatDistanceToNowStrict(
            row.experiment.createdAt,
            { addSuffix: true },
          );
          const createDateHuman = format(
            row.experiment.createdAt,
            "MMMM d, hh:mma",
          );

          return (
            <div className="text-placeholder truncate">
              <Tooltip content={createDateHuman} triggerAsChild>
                <span className="cursor-default" tabIndex={0}>
                  {createDateRelative}
                </span>
              </Tooltip>
            </div>
          );
        },
      },
      {
        key: "duration",
        header: "Duration",
        renderCell: (row) => {
          const duration = row.experiment.activatedAt
            ? formatDistanceStrict(
                row.experiment.activatedAt,
                row.experiment.completedAt ??
                  row.experiment.archivedAt ??
                  new Date(),
              )
            : "-";

          return <div className="text-placeholder truncate">{duration}</div>;
        },
      },
    ];
  }, [projectId]);

  if (experiments.length === 0) {
    return (
      <div className="text-placeholder flex h-[300px] items-center justify-center">
        No experiments yet.
      </div>
    );
  }

  return (
    <Table
      data={tableData}
      columns={columns}
      variant="bordered"
      scrollable={true}
      maxBodyHeight="calc(100vh - 250px)"
    />
  );
};
